import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";

import { Message, Template } from "@/types";
import { getStringContent } from "@/utils/utils";

const ROLE_LABEL_STYLES: Record<string, string> = {
  system: "bg-purple-100 text-purple-700",
  user: "bg-blue-100 text-blue-700",
  assistant: "bg-green-100 text-green-700",
  function: "bg-orange-100 text-orange-700",
  tool: "bg-orange-100 text-orange-700",
  developer: "bg-purple-100 text-purple-700",
  placeholder: "bg-gray-100 text-gray-700",
};

const formatRole = (role: string) => role.charAt(0).toUpperCase() + role.slice(1);

const getMessageText = (message: Message): string => {
  if (message.role === "assistant" && message.function_call) {
    return `Function call: ${message.function_call.name}\n${message.function_call.arguments}`;
  }
  return getStringContent(message);
};

interface MessageDiffProps {
  sourceTemplate: Template;
  targetTemplate: Template;
}

const MessageDiff = ({ sourceTemplate, targetTemplate }: MessageDiffProps) => {
  if (sourceTemplate.type === "completion" || targetTemplate.type === "completion") {
    const sourceText = sourceTemplate.type === "completion" ? getStringContent(sourceTemplate) : "";
    const targetText = targetTemplate.type === "completion" ? getStringContent(targetTemplate) : "";

    if (sourceText === targetText) {
      return <p className="p-6 text-center text-sm text-gray-400">No changes in prompt content</p>;
    }

    return (
      <div className="overflow-x-auto p-4">
        <ReactDiffViewer
          oldValue={sourceText}
          newValue={targetText}
          splitView
          hideLineNumbers
          showDiffOnly
          extraLinesSurroundingDiff={3}
          compareMethod={DiffMethod.WORDS}
        />
      </div>
    );
  }

  const sourceMessages =
    sourceTemplate.type === "chat" ? sourceTemplate.messages : ([] as Message[]);
  const targetMessages =
    targetTemplate.type === "chat" ? targetTemplate.messages : ([] as Message[]);
  const maxLen = Math.max(sourceMessages.length, targetMessages.length);

  if (maxLen === 0) {
    return <p className="p-4 text-center text-sm text-gray-400">No messages to compare</p>;
  }

  const hasAnyDiff =
    sourceMessages.some((msg, i) => {
      const targetMsg = targetMessages[i];
      if (!targetMsg) {
        return true;
      }
      return msg.role !== targetMsg.role || getMessageText(msg) !== getMessageText(targetMsg);
    }) || sourceMessages.length !== targetMessages.length;

  if (!hasAnyDiff) {
    return <p className="p-6 text-center text-sm text-gray-400">No changes in messages</p>;
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {Array.from({ length: maxLen }).map((_, index) => {
        const sourceMsg = sourceMessages[index];
        const targetMsg = targetMessages[index];
        const sourceText = sourceMsg ? getMessageText(sourceMsg) : "";
        const targetText = targetMsg ? getMessageText(targetMsg) : "";
        const sourceRole = sourceMsg?.role ?? "user";
        const targetRole = targetMsg?.role ?? "user";
        const isDifferent =
          sourceText !== targetText || sourceRole !== targetRole || !sourceMsg || !targetMsg;

        if (!isDifferent) {
          return null;
        }

        return (
          <div
            key={`msg-${sourceRole}-${targetRole}-${sourceText.slice(0, 30)}-${targetText.slice(0, 30)}`}
            className="rounded-lg border border-gray-200"
          >
            <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-1.5">
              <span
                className={`rounded px-2 py-0.5 text-xs font-semibold ${ROLE_LABEL_STYLES[sourceRole] ?? ROLE_LABEL_STYLES.user}`}
              >
                {formatRole(sourceRole)}
              </span>
              {sourceRole !== targetRole && targetMsg && (
                <>
                  <span className="text-xs text-gray-400">→</span>
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-semibold ${ROLE_LABEL_STYLES[targetRole] ?? ROLE_LABEL_STYLES.user}`}
                  >
                    {formatRole(targetRole)}
                  </span>
                </>
              )}
              {!sourceMsg && (
                <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">
                  Added
                </span>
              )}
              {!targetMsg && (
                <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-700">Removed</span>
              )}
            </div>
            <div className="overflow-x-auto">
              <ReactDiffViewer
                oldValue={sourceText}
                newValue={targetText}
                splitView
                hideLineNumbers
                showDiffOnly
                extraLinesSurroundingDiff={3}
                compareMethod={DiffMethod.WORDS}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MessageDiff;
